import { t } from '@/features/i18n/utils';
import z from 'zod';
import { WorkspaceBreadcrumbs } from '@/features/workspaces/components/workspace-breadcrumbs';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { useSuspenseQuery } from '@tanstack/react-query';
import { BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderChatButton } from '@/ui/components/header';
import { ChecklistRunDetailPage } from '@/features/checklists/components/checklist-run-detail-page';
import { useAnalysisResource } from '@/features/workspaces/helpers';
import { checklistRunQuery } from '@/features/checklists/queries';
import { workspaceQuery } from '@/features/workspaces/queries';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/_layout/(main)/checklists/$runId',
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const workspace = await context.queryClient.query({
      ...workspaceQuery(params.workspaceId),
      staleTime: 'static',
    });
    const run = await context.queryClient
      .query({
        ...checklistRunQuery(
          { documentType: workspace.type, resourceId: params.workspaceId },
          params.runId,
        ),
        staleTime: 'static',
      })
      .catch((error: unknown) => {
        if (isAxiosError(error) && error.response?.status === 404) return null;
        throw error;
      });
    if (!run) throw notFound();
  },
  validateSearch: z.object({ itemId: z.string().optional() }),
});

function RouteComponent() {
  const { workspaceId, projectId, runId } = Route.useParams();
  const resource = useAnalysisResource();
  const { data: run } = useSuspenseQuery(checklistRunQuery(resource, runId));

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/workspaces/$workspaceId/checklists"
          params={{ projectId, workspaceId }}
        />
        <WorkspaceBreadcrumbs>
          <BreadcrumbLink
            to="/project/$projectId/workspaces/$workspaceId/checklists"
            params={{ projectId, workspaceId }}
          >
            {t({ en: 'Checklists', nb: 'Sjekklister' })}
          </BreadcrumbLink>
          <BreadcrumbPage>
            <span className="line-clamp-1">{run.name}</span>
          </BreadcrumbPage>
        </WorkspaceBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <ChecklistRunDetailPage />
    </>
  );
}
